import { baseApi, Tag } from './base'
import { Document } from '../models/document.model'
import { Establishment, UpdateEstablishmentDto } from '../models/establishment.model'
import { IdentityJustificationTypeItem } from '../models/identity-justification.model'
import { CreateTrustedActorDto, TrustedActor } from '../models/trusted-actor.model'
import { ExternalHealthDataWorklist } from '../models/external-health-data-worklist.model'
import { cacheByIdArg } from '@/utils/rtkQueryCacheUtils'

export const establishmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEstablishment: builder.query<Establishment, string>({
      query: (establishmentId) => `/establishments/${establishmentId}`,
      providesTags: cacheByIdArg(Tag.ESTABLISHMENT),
    }),
    getEstablishmentBackgrounds: builder.query<Document[], { establishmentId: string; key?: string }>({
      query: ({ establishmentId, key }) => ({
        url: `/establishments/${establishmentId}/backgrounds`,
        method: 'GET',
        params: key ? { key } : undefined,
      }),
      providesTags: (result, error, arg) => [{ type: Tag.ESTABLISHMENT, id: arg.establishmentId }],
    }),
    updateEstablishment: builder.mutation<Establishment, { id: string; data: UpdateEstablishmentDto }>({
      query: ({ id, data }) => ({
        url: `/establishments/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),
    updateIdentityJustificationTypes: builder.mutation<
      IdentityJustificationTypeItem,
      { establishmentId: string; item: IdentityJustificationTypeItem }
    >({
      query: ({ establishmentId, item }) => ({
        url: `establishments/${establishmentId}/identity-justification-types`,
        method: 'PATCH',
        body: item,
      }),
      invalidatesTags: (result, error, arg) =>
        arg.establishmentId
          ? [
            { type: Tag.IDENTITY_JUSTIFICATION_TYPES, id: arg.establishmentId },
            { type: Tag.ESTABLISHMENT, id: arg.establishmentId },
          ]
          : [],
    }),
    deleteIdentityJustificationType: builder.mutation<void, { establishmentId: string; _id: string }>({
      query: ({ establishmentId, _id }) => ({
        url: `establishments/${establishmentId}/identity-justification-types`,
        method: 'DELETE',
        body: { _id },
      }),
      invalidatesTags: (result, error, arg) =>
        arg.establishmentId
          ? [
            { type: Tag.IDENTITY_JUSTIFICATION_TYPES, id: arg.establishmentId },
            { type: Tag.ESTABLISHMENT, id: arg.establishmentId },
          ]
          : [],
    }),
    listTrustedActors: builder.query<
      { items: TrustedActor[]; totalCount: number },
      { establishmentId: string; limit?: number; skip?: number }
    >({
      query: ({ establishmentId, limit, skip }) => ({
        url: `/establishments/${establishmentId}/trusted-actors`,
        method: 'GET',
        params: { limit, skip },
      }),
      providesTags: (result, error, arg) => [{ type: Tag.TRUSTED_ACTORS, id: arg.establishmentId }],
    }),
    addTrustedActor: builder.mutation<TrustedActor, { establishmentId: string; dto: CreateTrustedActorDto }>({
      query: ({ establishmentId, dto }) => ({
        url: `/establishments/${establishmentId}/trusted-actors`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: (result, error, arg) => [{ type: Tag.TRUSTED_ACTORS, id: arg.establishmentId }],
    }),
    updateTrustedActor: builder.mutation<TrustedActor, { establishmentId: string; actorId: string; dto: CreateTrustedActorDto }>({
      query: ({ establishmentId, actorId, dto }) => ({
        url: `/establishments/${establishmentId}/trusted-actors/${actorId}`,
        method: 'PATCH',
        body: dto,
      }),
      invalidatesTags: (result, error, arg) => [{ type: Tag.TRUSTED_ACTORS, id: arg.establishmentId }],
    }),
    removeTrustedActor: builder.mutation<string, { establishmentId: string; actorId: string }>({
      query: ({ establishmentId, actorId }) => ({
        url: `/establishments/${establishmentId}/trusted-actors/${actorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: Tag.TRUSTED_ACTORS, id: arg.establishmentId }],
    }),
    listExternalHealthDataWorklist: builder.query<
      { items: ExternalHealthDataWorklist[]; totalCount: number },
      { establishmentId: string; limit?: number; skip?: number }
    >({
      query: ({ establishmentId, limit, skip }) => ({
        url: `/establishments/${establishmentId}/ins-worklist`,
        method: 'GET',
        params: { limit, skip },
      }),
      providesTags: (result, error, arg) => [{ type: Tag.INS_WORKLIST, id: arg.establishmentId }],
    }),
    ingestExternalIdentity: builder.mutation<
      {
        status: 'QUARANTINED'
        worklistEntryId?: string
      },
      {
        establishmentId: string
        emitter?: string
        insNIR: string
        insOID: string
        traits: {
          birthName: string
          firstName: string
          birthFirstName: string
          birthDate: string
          sex: string
          birthPlaceCode: string
        }
      }
    >({
      query: ({ establishmentId, ...body }) => ({
        url: `/establishments/${establishmentId}/ins-worklist`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.INS_WORKLIST, id: arg.establishmentId }],
    }),
    registerIdentityVerificationKo: builder.mutation<
      { worklistEntryId?: string },
      { establishmentId: string; patient: string; patientFullName?: string; insNIR: string; insOID?: string }
    >({
      query: ({ establishmentId, ...body }) => ({
        url: `/establishments/${establishmentId}/ins-worklist/verification-ko`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.INS_WORKLIST, id: arg.establishmentId }],
    }),
  }),
  overrideExisting: false,
})
